// Copy items and recipes from another venue (spec/13 §6.4; ADR-0040 §9; `inventory:manage` here, and the member works
// at both venues): a chain that wants its venues the same starts each one from another's definitions. The dialog says
// plainly what copies — the items, their pack sizes, their par levels and the recipes — and what never does — a
// quantity, a cost, any history or a retail item. Choosing the venue runs the copy as a dry run, which writes nothing
// and answers exactly what it would do, item by item and recipe by recipe: new here, matched to an item here of the
// same name and unit, copied before, or left out. Running it again copies nothing twice; the answer says what happened.

import type { StockCopyResult } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, SelectField, Skeleton, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useMyVenues } from '../../api/queries';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Notice } from '../../ui';
import { useCopyFromVenue, useCopyPreview } from './queries';
import { useProblem } from '../../api/use-problem';

type ItemOutcome = StockCopyResult['items'][number]['outcome'];
type RecipeOutcome = StockCopyResult['recipes'][number]['outcome'];

/** The order the outcomes are listed in: what changes here first, what is left out last. */
const ITEM_OUTCOMES: readonly ItemOutcome[] = ['copy', 'match', 'already_copied', 'skip_unit', 'skip_retail'];
const RECIPE_OUTCOMES: readonly RecipeOutcome[] = ['copy', 'already_here', 'skip'];

export function CopyFromVenueDialog({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} size="md" title={t('stock.copy.title')} description={t('stock.copy.hint')}>
      {open ? <CopyForm onDone={onClose} /> : null}
    </Dialog>
  );
}

function CopyForm({ onDone }: { readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const venues = useMyVenues();
  const others = (venues.data ?? []).filter((one) => one.organizationId === venue.organizationId && one.id !== venue.id);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const preview = useCopyPreview(venue.organizationId, venue.id, sourceId);
  const copy = useCopyFromVenue(venue.organizationId, venue.id);
  const [result, setResult] = useState<StockCopyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sourceName = others.find((one) => one.id === sourceId)?.name ?? '';

  if (result !== null) {
    return (
      <div className="console-form">
        <Notice tone="ok" role="status" title={t('stock.copy.doneTitle', { venue: sourceName })}>
          <p>{t('stock.copy.doneBody', { items: result.itemsCopied, recipes: result.recipesCopied })}</p>
          {result.itemsMatched > 0 ? <p>{t('stock.copy.matched', { count: result.itemsMatched })}</p> : null}
          {result.itemsAlreadyHere + result.recipesAlreadyHere > 0 ? <p>{t('stock.copy.already', { items: result.itemsAlreadyHere, recipes: result.recipesAlreadyHere })}</p> : null}
        </Notice>
        <CopyOutcomes result={result} />
        <p className="console-subtle">{t('stock.copy.next')}</p>
        <div className="console-form__actions console-form__actions--end">
          <Button variant="primary" onClick={onDone}>
            {t('common.done')}
          </Button>
        </div>
      </div>
    );
  }

  if (venues.data !== undefined && others.length === 0) {
    return (
      <div className="console-form">
        <Notice role="note">{t('stock.copy.noOther')}</Notice>
        <div className="console-form__actions console-form__actions--end">
          <Button variant="secondary" onClick={onDone}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    );
  }

  const nothing = preview.data !== undefined && preview.data.itemsCopied + preview.data.recipesCopied === 0;

  return (
    <form
      className="console-form"
      aria-label={t('stock.copy.title')}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (sourceId === null) {
          setError(t('stock.copy.chooseVenue'));
          return;
        }
        setError(null);
        copy.mutate(
          { sourceVenueId: sourceId },
          {
            onSuccess: (answer) => {
              setResult(answer);
              show({ tone: 'ok', title: t('stock.copy.toast') });
            },
            onError: (failure) => setError(problem.text(failure, { fallback: t('stock.copy.refused') })),
          },
        );
      }}
    >
      {error !== null ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <SelectField
        label={t('stock.copy.from')}
        placeholder={t('stock.copy.fromPlaceholder')}
        value={sourceId ?? ''}
        onValueChange={(value) => setSourceId(value)}
        options={others.map((one) => ({ value: one.id, label: one.name }))}
      />
      <div className="console-stock-copy">
        <div className="console-stock-copy__col">
          <h3 className="console-stock-copy__title">{t('stock.copy.copiesTitle')}</h3>
          <ul className="console-stock-copy__list">
            {(['items', 'packs', 'par', 'recipes'] as const).map((point) => (
              <li key={point}>
                <span className="console-stock-copy__mark console-stock-copy__mark--yes" aria-hidden="true">
                  <Icon name="check" />
                </span>
                {t(`stock.copy.copies.${point}`)}
              </li>
            ))}
          </ul>
        </div>
        <div className="console-stock-copy__col">
          <h3 className="console-stock-copy__title">{t('stock.copy.neverTitle')}</h3>
          <ul className="console-stock-copy__list">
            {(['quantities', 'costs', 'history', 'retail'] as const).map((point) => (
              <li key={point}>
                <span className="console-stock-copy__mark" aria-hidden="true">
                  <Icon name="close" />
                </span>
                {t(`stock.copy.never.${point}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {sourceId === null ? null : preview.data === undefined ? (
        preview.isError ? (
          <Notice role="note">{t('stock.copy.previewUnavailable', { venue: sourceName })}</Notice>
        ) : (
          <Skeleton shape="text" lines={2} />
        )
      ) : (
        <>
          <Notice role="status" title={t('stock.copy.previewTitle', { venue: sourceName })}>
            <p>{nothing ? t('stock.copy.previewNothing') : t('stock.copy.preview', { items: preview.data.itemsCopied, recipes: preview.data.recipesCopied })}</p>
            {preview.data.itemsMatched > 0 ? <p>{t('stock.copy.wouldMatch', { count: preview.data.itemsMatched })}</p> : null}
            {preview.data.itemsSkipped > 0 ? <p>{t('stock.copy.wouldSkip', { count: preview.data.itemsSkipped })}</p> : null}
            <p className="console-subtle">{t('stock.copy.idempotent')}</p>
          </Notice>
          <CopyOutcomes result={preview.data} />
        </>
      )}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={copy.isPending} disabled={nothing}>
          {t('stock.copy.submit')}
        </Button>
      </div>
    </form>
  );
}

/** Each source item and recipe under what becomes of it — the dry run's answer before the copy, the copy's after. */
function CopyOutcomes({ result }: { readonly result: StockCopyResult }): ReactNode {
  const { t } = useTranslation('console');
  const groups = [
    ...ITEM_OUTCOMES.map((outcome) => ({ key: `item-${outcome}`, label: t(`stock.copy.itemOutcome.${outcome}`), names: result.items.filter((one) => one.outcome === outcome).map((one) => one.name) })),
    ...RECIPE_OUTCOMES.map((outcome) => ({ key: `recipe-${outcome}`, label: t(`stock.copy.recipeOutcome.${outcome}`), names: result.recipes.filter((one) => one.outcome === outcome).map((one) => one.name) })),
  ].filter((group) => group.names.length > 0);
  if (groups.length === 0) return null;
  return (
    <dl className="console-stock-copy__outcomes" aria-label={t('stock.copy.outcomesLabel')}>
      {groups.map((group) => (
        <div key={group.key} className="console-stock-copy__outcome">
          <dt>
            {group.label} <span className="console-stock-copy__count">{group.names.length}</span>
          </dt>
          <dd>
            <ul className="console-stock-copy__names">
              {group.names.map((name, index) => (
                <li key={`${name}-${String(index)}`}>{name}</li>
              ))}
            </ul>
          </dd>
        </div>
      ))}
    </dl>
  );
}
